/* ── The fence, in 3D ─────────────────────────────────────────────────────
   A quiet tabletop scene in the same dark-metal language as the hero
   movement: the site at the middle of a ground disc, a dashed brass
   fence ring slowly rotating around it like a surveyed boundary, and
   two punch pins — one inside (accepted), one far outside (declined).
   Pointer parallax; pauses off-screen; static frame under reduced motion. */

import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { useEffect, useRef } from 'react';

const INK = 0x0b0d12;

export default function Fence3D({ reduced }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || typeof WebGLRenderingContext === 'undefined') return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    } catch (e) {
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene(); // no fog: it tone-maps lighter than the page ink; the CSS mask does the fading

    const camera = new THREE.PerspectiveCamera(38, 1, 1, 3000);
    camera.position.set(0, 300, 520);

    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTexture;

    /* Materials — same metals as the hero movement */
    const brass = new THREE.MeshStandardMaterial({ color: 0x63512f, metalness: 0.85, roughness: 0.44, envMapIntensity: 0.2 });
    const steel = new THREE.MeshStandardMaterial({ color: 0x333a49, metalness: 0.7, roughness: 0.5, envMapIntensity: 0.14 });
    // unlit, so it stays a whisper regardless of lighting
    const zoneMat = new THREE.MeshBasicMaterial({
      color: 0xb9a079, transparent: true, opacity: 0.05, depthWrite: false,
    });
    const tickMat = new THREE.MeshStandardMaterial({ color: 0x232a38, metalness: 0.5, roughness: 0.7, envMapIntensity: 0.08 });
    const okMat = new THREE.MeshStandardMaterial({ color: 0x3f7f5c, metalness: 0.35, roughness: 0.5, envMapIntensity: 0.2 });
    const badMat = new THREE.MeshStandardMaterial({ color: 0x83434c, metalness: 0.35, roughness: 0.5, envMapIntensity: 0.2 });
    const materials = [brass, steel, zoneMat, tickMat, okMat, badMat];

    /* Lights — warm key, cool rim */
    scene.add(new THREE.HemisphereLight(0x343b4a, INK, 0.28));
    const key = new THREE.DirectionalLight(0xffdfb0, 0.7);
    key.position.set(-320, 420, 380);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x8797bd, 0.35);
    rim.position.set(380, 200, -320);
    scene.add(rim);

    const root = new THREE.Group();
    scene.add(root);

    /* The zone inside the fence — a whisper of brass on the ground plane */
    const zone = new THREE.Mesh(new THREE.CircleGeometry(150, 64), zoneMat);
    zone.rotation.x = -Math.PI / 2;
    zone.position.y = 0.5;
    root.add(zone);

    /* Dashed fence ring — rotates slowly, like a surveyed boundary */
    const fence = new THREE.Group();
    const dashGeo = new THREE.BoxGeometry(16, 7, 2.5);
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      const dash = new THREE.Mesh(dashGeo, brass);
      dash.position.set(Math.cos(a) * 150, 3.5, Math.sin(a) * 150);
      dash.rotation.y = -a + Math.PI / 2;
      fence.add(dash);
    }
    root.add(fence);

    /* Surveyor ticks just outside the fence */
    const tickGeo = new THREE.BoxGeometry(1.6, 1, 9);
    for (let i = 0; i < 72; i++) {
      const a = (i / 72) * Math.PI * 2;
      const tick = new THREE.Mesh(tickGeo, tickMat);
      tick.position.set(Math.cos(a) * 182, 0.5, Math.sin(a) * 182);
      tick.rotation.y = -a + Math.PI / 2;
      root.add(tick);
    }

    /* The site */
    const site = new THREE.Group();
    const main = new THREE.Mesh(new THREE.BoxGeometry(52, 34, 40), steel);
    main.position.y = 17;
    site.add(main);
    const upper = new THREE.Mesh(new THREE.BoxGeometry(34, 14, 26), steel);
    upper.position.set(-4, 41, 0);
    site.add(upper);
    const door = new THREE.Mesh(new THREE.BoxGeometry(10, 16, 2), brass);
    door.position.set(8, 8, 20.5);
    site.add(door);
    root.add(site);

    /* Punch pins: a ball on a stem */
    function makePin(material, x, z) {
      const pin = new THREE.Group();
      const stem = new THREE.Mesh(new THREE.CylinderGeometry(1.6, 1.6, 30, 12), material);
      stem.position.y = 15;
      pin.add(stem);
      const ball = new THREE.Mesh(new THREE.SphereGeometry(7.5, 20, 16), material);
      ball.position.y = 33;
      pin.add(ball);
      const foot = new THREE.Mesh(new THREE.CylinderGeometry(5, 5, 1.2, 20), material);
      foot.position.y = 0.6;
      pin.add(foot);
      pin.position.set(x, 0, z);
      return pin;
    }
    root.add(makePin(okMat, 78, 52));      // 94 units out — inside the fence
    root.add(makePin(badMat, -222, -82));  // 237 units out — far outside

    /* Layout */
    const layout = () => {
      const W = Math.max(1, mount.clientWidth);
      const H = Math.max(1, mount.clientHeight);
      renderer.setSize(W, H);
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
    };
    layout();
    const ro = new ResizeObserver(layout);
    ro.observe(mount);

    /* Pointer parallax (fine pointers only) */
    let px = 0, py = 0;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const onPointer = (e) => {
      px = (e.clientX / window.innerWidth) * 2 - 1;
      py = (e.clientY / window.innerHeight) * 2 - 1;
    };
    if (finePointer && !reduced) window.addEventListener('pointermove', onPointer, { passive: true });

    let visible = true;
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (!reduced) renderer.setAnimationLoop(visible ? animate : null);
    });
    io.observe(mount);

    const clock = new THREE.Clock();

    function pose(t) {
      fence.rotation.y = t * 0.06;
      root.rotation.y = Math.sin(t * 0.12) * 0.05;
    }

    function animate() {
      const dt = Math.min(clock.getDelta(), 0.1);
      pose(clock.elapsedTime);
      camera.position.x += (px * 34 - camera.position.x) * Math.min(1, dt * 3);
      camera.position.y += (300 - py * 24 - camera.position.y) * Math.min(1, dt * 3);
      camera.lookAt(0, 10, 0);
      renderer.render(scene, camera);
    }

    if (reduced) {
      pose(2.2);
      camera.lookAt(0, 10, 0);
      renderer.render(scene, camera);
    } else {
      renderer.setAnimationLoop(animate);
    }

    return () => {
      renderer.setAnimationLoop(null);
      io.disconnect();
      ro.disconnect();
      if (finePointer && !reduced) window.removeEventListener('pointermove', onPointer);
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
      });
      materials.forEach((m) => m.dispose());
      envTexture.dispose();
      pmrem.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [reduced]);

  return <div ref={mountRef} className="fence3d" aria-hidden="true" />;
}
